import { useTheme } from "@/hooks/useTheme";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { INVESTOR_SLIDES } from "@/lib/constants";
import type { SlideEntry } from "@/lib/constants";

const EXPANDED_W = 240;
const COLLAPSED_W = 56;

interface DeckMeta {
  id: string;
  name: string;
  date: string;
}

interface EditorSidebarProps {
  activeSlide: number;
  onSlideClick: (index: number) => void;
  isCollapsed: boolean;
  onToggle: () => void;
  slides?: readonly SlideEntry[];
  deckMeta?: DeckMeta;
}

export function EditorSidebar({
  activeSlide,
  onSlideClick,
  isCollapsed,
  onToggle,
  slides = INVESTOR_SLIDES,
  deckMeta = { id: "CX-002-A", name: "COSX_PITCH_DECK", date: "2026.03.15" },
}: EditorSidebarProps) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const strokeColor = isDark ? "#3a3a3a" : "#a1a1aa";
  const [showInfo, setShowInfo] = useState(false);
  const infoBtnRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  // Position tooltip relative to info button
  useEffect(() => {
    if (!showInfo || !infoBtnRef.current) return;
    const rect = infoBtnRef.current.getBoundingClientRect();
    const tooltipHeight = 60; // approximate
    const top = Math.min(rect.top - 4, window.innerHeight - tooltipHeight - 16);
    setTooltipPos({
      top: Math.max(8, top),
      left: rect.right + 8,
    });
  }, [showInfo]);

  // Close tooltip on outside click
  useEffect(() => {
    if (!showInfo) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        infoBtnRef.current && !infoBtnRef.current.contains(target) &&
        tooltipRef.current && !tooltipRef.current.contains(target)
      ) {
        setShowInfo(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showInfo]);

  return (
    <aside
      className="fixed top-[var(--topnav-height)] left-0 z-[100] flex flex-col font-[family-name:var(--font-mono)] transition-[width] duration-200 ease-in-out overflow-hidden"
      style={{
        height: "calc(100dvh - var(--topnav-height))",
        width: isCollapsed ? COLLAPSED_W : EXPANDED_W,
        background: isDark ? "#0f0f0f" : "#f4f4f5",
        borderRight: `1px solid ${strokeColor}`,
      }}
    >
      {/* Top: Back to documents */}
      <div
        className="shrink-0 flex items-center h-[36px]"
        style={{ borderBottom: `1px solid ${strokeColor}` }}
      >
        <Link
          to="/ir/dataroom/docs"
          className="flex items-center gap-1.5 w-full h-full px-3 text-[11px] tracking-wider uppercase text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors whitespace-nowrap overflow-hidden"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {!isCollapsed && <span>Documents</span>}
        </Link>
      </div>

      {/* Middle: Slide nav (vertically centered) */}
      <div className="flex-1 flex flex-col justify-center px-1">
        <nav className="flex flex-col gap-0.5">
          {slides.map((slide, i) => {
            const isActive = activeSlide === i;
            const num = String(i + 1).padStart(2, "0");
            return (
              <button
                key={slide.id}
                onClick={() => onSlideClick(i)}
                className="text-left px-3 py-2 text-[11px] tracking-wider uppercase transition-colors rounded-sm whitespace-nowrap overflow-hidden"
                style={{
                  borderLeft: isActive ? "2px solid var(--color-accent)" : "2px solid transparent",
                  color: isActive ? "var(--color-fg)" : "var(--color-fg-tertiary)",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {isCollapsed ? num : `${num}. ${slide.label}`}
              </button>
            );
          })}
        </nav>

      </div>

      {/* Document name */}
      {!isCollapsed && (
        <div
          className="shrink-0 px-5 py-2 text-[11px] tracking-wider uppercase text-[var(--color-fg-secondary)]"
          style={{ borderTop: `1px solid ${strokeColor}` }}
        >
          {deckMeta.name}
        </div>
      )}

      {/* Bottom: Metadata */}
      {isCollapsed ? (
        <div className="px-3 pb-3">
          <button
            ref={infoBtnRef}
            onClick={() => setShowInfo((v) => !v)}
            className="w-full flex items-center justify-center text-[13px] text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors py-1"
            aria-label="Deck info"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
          {showInfo && createPortal(
            <div
              ref={tooltipRef}
              className="fixed px-3 py-2 rounded text-[9px] font-[family-name:var(--font-mono)] tracking-wider uppercase whitespace-nowrap flex flex-col gap-1 text-[var(--color-fg-tertiary)] z-[200]"
              style={{
                top: tooltipPos.top,
                left: tooltipPos.left,
                background: isDark ? "#1a1a1a" : "#ffffff",
                border: `1px solid ${strokeColor}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              <span>DECK_ID: {deckMeta.id}</span>
              <span>DATE: {deckMeta.date}</span>
              <span>STATUS: CONFIDENTIAL</span>
            </div>,
            document.body
          )}
        </div>
      ) : (
        <div className="px-5 pb-3 flex flex-col gap-1 text-[9px] tracking-wider uppercase text-[var(--color-fg-tertiary)]">
          <span>DECK_ID: {deckMeta.id}</span>
          <span>DATE: {deckMeta.date}</span>
          <span>STATUS: CONFIDENTIAL</span>
        </div>
      )}

      {/* Bottom: Toggle button */}
      <div
        className="shrink-0 flex items-center justify-center h-[36px]"
        style={{
          borderTop: `1px solid ${strokeColor}`,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <button
          onClick={onToggle}
          className="w-full h-full flex items-center justify-center text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors text-[13px] font-[family-name:var(--font-mono)]"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? "\u2192" : "\u2190"}
        </button>
      </div>
    </aside>
  );
}

export { EXPANDED_W, COLLAPSED_W };
